/**
 * Product analytics: the one place screens report what the user *did*, and
 * the only module that talks to PostHog.
 *
 * Event names are the PostHog history apps/web built (apps/web/src/analytics/
 * events.ts) — keep them byte for byte so dashboards keep working. Market Spy
 * is retired, so its event is gone.
 *
 * Rules (capability-parity.md §0):
 *  - PostHog is loaded and initialised at most once, lazily, and ONLY when the
 *    visitor accepted analytics (cookie `lemon_analytics_consent=accepted`)
 *    and this tab is not an administrator's support session. Otherwise the
 *    library is never even downloaded, so nothing can be sent.
 *  - The same gate runs again on every outgoing event (`before_send`), so
 *    withdrawing consent or turning this tab into a support session mid-page
 *    stops capture at once — autocaptured pageviews included.
 *  - On lemon-ai.com hosts, events go through the first-party `/ingest` proxy.
 *  - Every event carries `journey_id` (funnelJourney.ts).
 *  - Secrets that arrive in links (signup/reset secrets, support tokens) and
 *    the email address are masked in every URL PostHog sends — events and the
 *    `/flags` person properties alike (urlSecrets.ts).
 *
 * Nothing here ever throws or rejects: analytics is not worth failing a
 * user's action over, and a blocked PostHog is normal for many visitors.
 */
import type { PostHog, CaptureResult } from 'posthog-js';
import type { Account, CurrentUser } from '@/api/types';
import { isSupportSessionTab } from '@/auth/session';
import { funnelJourneyId } from './funnelJourney';
import { maskEventSecrets, maskStoredInitialPersonInfo } from './urlSecrets';

export const ANALYTICS_EVENTS = {
  signedIn: 'user_signed_in',
  signupStarted: 'signup_started',
  signupCompleted: 'signup_completed',
  signupFailed: 'signup_failed',

  assetPerformanceFiltersApplied: 'asset_performance_filters_applied',
  assetOpened: 'asset_opened',
  assetReportExported: 'asset_report_exported',

  attributeAnalysisRun: 'attribute_analysis_run',

  // Pre-dates the apps/web module and has history in PostHog under this exact name.
  creativeLabMessageSent: 'creative_lab_message_sent',
  creativeGenerated: 'creative_generated',

  predictionRun: 'prediction_run',

  connectionAdded: 'connection_added',
  connectionRemoved: 'connection_removed',

  alertConfigured: 'alert_configured',
  targetMetricsSaved: 'target_metrics_saved',
  imageResized: 'image_resized',

  userInvited: 'user_invited',
  apiKeyCreated: 'api_key_created',

  sampleDataEntered: 'sample_data_entered',
  sampleDataExited: 'sample_data_exited',

  tourStarted: 'tour_started',
  tourCompleted: 'tour_completed',
  tourDismissed: 'tour_dismissed',
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

export type AnalyticsProperties = Record<string, string | number | boolean | null>;

// --- Configuration (the same project and settings as apps/web) ---------------

const POSTHOG_PROJECT_KEY = 'phc_MSKBCupnjtk0mBzA2ICEGQxS8aCIB6T33BYc59HbDEA'; // gitleaks:allow (public PostHog project key)
const CONSENT_PATTERN = /(?:^|; )lemon_analytics_consent=([^;]*)/;

export type AnalyticsConsent = 'accepted' | 'declined' | 'pending';

/** The visitor's choice, shared with the marketing site through one cookie. */
export const analyticsConsent = (): AnalyticsConsent => {
  const match = CONSENT_PATTERN.exec(document.cookie);
  let value: string;
  try {
    value = match?.[1] ? decodeURIComponent(match[1]) : '';
  } catch {
    value = '';
  }
  return value === 'accepted' || value === 'declined' ? value : 'pending';
};

/** The one gate: consent given, and not an administrator viewing as a customer. */
export const captureAllowed = (): boolean => analyticsConsent() === 'accepted' && !isSupportSessionTab();

/** lemon-ai.com hosts send through the first-party `/ingest` proxy; anything else (local, previews) goes direct. */
export const postHogApiHost = (hostname: string): string =>
  hostname === 'lemon-ai.com' || hostname.endsWith('.lemon-ai.com') ? '/ingest' : 'https://us.i.posthog.com';

/** Runs on every event PostHog is about to send: re-check the gate, mask URLs. */
export const beforeSend = (event: CaptureResult | null): CaptureResult | null => {
  if (!event || !captureAllowed()) return null;
  return maskEventSecrets(event);
};

// --- The client ------------------------------------------------------------------

let client: Promise<PostHog | null> | null = null;

const loadClient = (): Promise<PostHog | null> => {
  client ??= initialise().catch(() => {
    client = null; // A blocked download may succeed on a later call.
    return null;
  });
  return client;
};

const initialise = async (): Promise<PostHog | null> => {
  const { default: posthog } = await import('posthog-js');
  posthog.init(POSTHOG_PROJECT_KEY, {
    api_host: postHogApiHost(window.location.hostname),
    ui_host: 'https://us.posthog.com',
    defaults: '2025-05-24',
    // Kept apart from the shared marketing preference (as in apps/web); the
    // cookie above is the only input to it.
    consent_persistence_name: '__ph_opt_in_out_lemon_product',
    before_send: beforeSend,
    // `/flags` sends the stored initial URL and referrer, bypassing before_send.
    loaded: (instance) => { maskStoredInitialPersonInfo(instance); },
  });
  // A visit to apps/web before consent was given persists an opt-out under
  // the same name; the visitor has since accepted, so opt back in.
  posthog.opt_in_capturing({ captureEventName: false });
  return posthog;
};

/**
 * The PostHog client, or null when capture is not allowed right now. Loaded on
 * first use so public screens only download it when they emit something.
 */
const posthogClient = (): Promise<PostHog | null> => {
  return captureAllowed() ? loadClient() : Promise.resolve(null);
};

/** Run against the client when capture is allowed; swallow every failure. */
const withClient = async (run: (posthog: PostHog) => void): Promise<void> => {
  try {
    const posthog = await posthogClient();
    if (posthog && captureAllowed()) run(posthog);
  } catch {
    // Intentionally silent: a console error per blocked capture is noise.
  }
};

// --- Public API ---------------------------------------------------------------

/** Report a user action. Never send filter values or other user data — keys only. */
export function track(event: AnalyticsEvent, properties?: AnalyticsProperties): void {
  void withClient((posthog) => {
    posthog.capture(event, { ...properties, journey_id: funnelJourneyId() });
  });
}

/**
 * Name the signed-in person (apps/web store/index.ts fetchMe). Skipped in a
 * support session: the admin console reads these to answer whether the
 * customer uses the product, and a support visit is not that.
 */
export function identifyUser(user: CurrentUser): void {
  if (user.support_session) return;
  void withClient((posthog) => {
    posthog.identify(user.email_address, {
      id: user.id,
      email: user.email_address,
      name: user.name,
      surname: user.surname,
      role: user.role,
    });
  });
}

/**
 * Bind the person to the account paying for them (apps/web analytics/events.ts
 * identifyAccount): the group makes the account an entity in PostHog; the
 * person property is what a query can filter on retroactively.
 */
export function identifyAccount(account: Pick<Account, 'id' | 'name'>): void {
  void withClient((posthog) => {
    posthog.group('account', account.id, { name: account.name });
    posthog.setPersonProperties({ account_id: account.id });
  });
}

/**
 * Forget the person on logout. `resetGroups` as well as `reset`: the account
 * group survives `reset` alone, so on a shared browser the next person's
 * events would be filed under the previous account. Call it BEFORE the
 * support-tab marker is cleared, so loading the client here captures nothing.
 */
export async function resetAnalytics(): Promise<void> {
  try {
    // A persisted identity from an earlier page load needs the client to clear it.
    const posthog = await (analyticsConsent() === 'accepted' ? loadClient() : client);
    if (!posthog) return;
    posthog.resetGroups();
    posthog.reset();
  } catch {
    // As above.
  }
}

/**
 * PostHog's id for this visitor, sent with signup so the server-side
 * conversion joins the same person. Null without consent (never invented).
 */
export async function analyticsDistinctId(): Promise<string | null> {
  try {
    const posthog = await posthogClient();
    return posthog && captureAllowed() ? posthog.get_distinct_id() : null;
  } catch {
    return null;
  }
}
